import { Button } from "@legal-500/legal500-design-system";
import { downloadResultsCsv } from "./moveBpaCsv.js";
import { MoveBpaRowResults } from "./MoveBpaRowResults.js";
import type { MoveBpaProgress, MoveBpaRowResult, MoveBpaTotals } from "./useMoveBpaBatch.js";
import styles from "../PipelineBrowser.module.css";

type MoveBpaRunReportProps = {
  totals: MoveBpaTotals;
  results: MoveBpaRowResult[];
  progress: MoveBpaProgress;
  dryRun: boolean;
  stopped: boolean;
};

export function MoveBpaRunReport({
  totals,
  results,
  progress,
  dryRun,
  stopped,
}: MoveBpaRunReportProps) {
  const counters = [
    { label: "Rows ok", value: totals.ok },
    { label: "Rows partial (approval sync failed for some families)", value: totals.partial },
    { label: "Rows failed", value: totals.failed },
    ...(totals.unknown > 0
      ? [{ label: "Rows unknown (rerun to confirm)", value: totals.unknown }]
      : []),
    { label: dryRun ? "Roots that would move" : "Roots matched", value: totals.roots },
    { label: dryRun ? "Children that would move" : "Children matched", value: totals.children },
    { label: "Already in place (skipped)", value: totals.alreadyInPlace },
    ...(dryRun
      ? []
      : [
          { label: "Approval requests moved", value: totals.movedApprovalRequests },
          { label: "Testimonials moved", value: totals.movedTestimonials },
          { label: "Processing items moved", value: totals.movedProcessingItems },
        ]),
    { label: "Approval sync failures", value: totals.approvalFailures },
    { label: "Families needing rerun", value: totals.familiesNeedingRerun },
    { label: "Warnings", value: totals.warnings },
  ];
  const unsent = progress.totalRows - progress.processedRows;

  return (
    <div className={styles.syncStatePanel}>
      <div className={styles.contentSectionTitle}>
        Result: {dryRun ? "dry run (nothing written)" : "moved"}
        {stopped ? ", stopped by user" : ""}
      </div>
      <div className={styles.syncStateMessage}>
        Processed {progress.processedRows} / {progress.totalRows} rows.
        {unsent > 0 ? ` ${unsent} rows were not sent.` : ""}
      </div>
      <table className={styles.reportTable}>
        <tbody>
          {counters.map(counter => (
            <tr key={counter.label}>
              <td>{counter.label}</td>
              <td>{counter.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {results.length > 0 && <MoveBpaRowResults results={results} dryRun={dryRun} />}
      <div className={styles.syncStateRow}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          disabled={results.length === 0}
          onClick={() => downloadResultsCsv(results, dryRun, new Date())}
        >
          Download results CSV ({results.length} rows)
        </Button>
      </div>
    </div>
  );
}
